/**
 * Tax-Loss Harvesting Calculator
 * Helps users identify and quantify tax-loss harvesting opportunities
 */
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, TrendingDown, DollarSign, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TaxLot {
  id: string;
  ticker: string;
  shares: number;
  purchaseDate: Date;
  purchasePrice: number;
  currentPrice: number;
  unrealizedGainLoss: number;
  isShortTerm: boolean;
}

interface TaxLossHarvestingCalculatorProps {
  holdings: Array<{
    ticker: string;
    shares: number;
    currentPrice: number;
    costBasis?: number;
    purchaseDate?: Date;
  }>;
  taxBracket?: number;
  className?: string;
}

export function TaxLossHarvestingCalculator({
  holdings,
  taxBracket = 0.24, // Default 24% federal bracket
  className,
}: TaxLossHarvestingCalculatorProps) {
  const [selectedLots, setSelectedLots] = useState<Set<string>>(new Set());
  const [customTaxBracket, setCustomTaxBracket] = useState(taxBracket * 100);
  const [stateTaxRate, setStateTaxRate] = useState(5);
  const [washSaleWarnings, setWashSaleWarnings] = useState<Set<string>>(new Set());

  // Generate tax lots from holdings
  const taxLots = useMemo<TaxLot[]>(() => {
    return holdings
      .filter(h => h.costBasis !== undefined)
      .map((h, idx) => {
        const purchaseDate = h.purchaseDate || new Date(Date.now() - 400 * 24 * 60 * 60 * 1000); // Default to ~13 months ago
        const isShortTerm = (Date.now() - purchaseDate.getTime()) < 365 * 24 * 60 * 60 * 1000;
        const costBasis = h.costBasis || h.currentPrice * 1.1; // Default to 10% loss if no cost basis
        const unrealizedGainLoss = (h.currentPrice - costBasis) * h.shares;

        return {
          id: `${h.ticker}-${idx}`,
          ticker: h.ticker,
          shares: h.shares,
          purchaseDate,
          purchasePrice: costBasis,
          currentPrice: h.currentPrice,
          unrealizedGainLoss,
          isShortTerm,
        };
      })
      .filter(lot => lot.unrealizedGainLoss < 0) // Only show losses
      .sort((a, b) => a.unrealizedGainLoss - b.unrealizedGainLoss); // Biggest losses first
  }, [holdings]);

  // Calculate tax savings for selected lots
  const calculations = useMemo(() => {
    const selectedLotsList = taxLots.filter(lot => selectedLots.has(lot.id));

    const shortTermLosses = selectedLotsList
      .filter(lot => lot.isShortTerm)
      .reduce((sum, lot) => sum + Math.abs(lot.unrealizedGainLoss), 0);

    const longTermLosses = selectedLotsList
      .filter(lot => !lot.isShortTerm)
      .reduce((sum, lot) => sum + Math.abs(lot.unrealizedGainLoss), 0);

    const totalLosses = shortTermLosses + longTermLosses;

    // Tax savings calculation
    // Short-term losses offset income at ordinary rates
    // Long-term losses offset at capital gains rates (typically lower)
    const effectiveTaxRate = (customTaxBracket + stateTaxRate) / 100;
    const longTermRate = Math.min(0.20, effectiveTaxRate * 0.6); // Simplified LTCG rate

    const shortTermSavings = shortTermLosses * effectiveTaxRate;
    const longTermSavings = longTermLosses * longTermRate;
    const totalSavings = shortTermSavings + longTermSavings;

    // Annual loss limit: $3,000 against ordinary income
    const annualLossLimit = 3000;
    const excessLoss = Math.max(0, totalLosses - annualLossLimit);
    const yearsToFullyUtilize = excessLoss > 0 ? Math.ceil(excessLoss / annualLossLimit) : 0;

    return {
      shortTermLosses,
      longTermLosses,
      totalLosses,
      shortTermSavings,
      longTermSavings,
      totalSavings,
      excessLoss,
      yearsToFullyUtilize,
      selectedCount: selectedLotsList.length,
    };
  }, [selectedLots, taxLots, customTaxBracket, stateTaxRate]);

  const toggleLot = (lotId: string) => {
    const newSelected = new Set(selectedLots);
    if (newSelected.has(lotId)) {
      newSelected.delete(lotId);
    } else {
      newSelected.add(lotId);
      // Check for wash sale risk
      const lot = taxLots.find(l => l.id === lotId);
      if (lot && lot.isShortTerm) {
        setWashSaleWarnings(prev => new Set(prev).add(lotId));
      }
    }
    setSelectedLots(newSelected);
  };

  const selectAll = () => {
    if (selectedLots.size === taxLots.length) {
      setSelectedLots(new Set());
      setWashSaleWarnings(new Set());
    } else {
      setSelectedLots(new Set(taxLots.map(l => l.id)));
      setWashSaleWarnings(new Set(taxLots.filter(l => l.isShortTerm).map(l => l.id)));
    }
  };

  const formatCurrency = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (absValue >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  if (taxLots.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Tax-Loss Harvesting Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-status-good mx-auto mb-3" />
          <p className="text-sm font-medium">No Harvesting Opportunities</p>
          <p className="text-xs text-muted-foreground mt-1">
            All positions are currently at a gain or no cost basis data is available.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("col-span-full", className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Tax-Loss Harvesting Calculator
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {taxLots.length} opportunities
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Select positions with unrealized losses to calculate potential tax savings.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Tax Rate Inputs */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Federal Tax Bracket</Label>
            <Select
              value={customTaxBracket.toString()}
              onValueChange={(v) => setCustomTaxBracket(Number(v))}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10%</SelectItem>
                <SelectItem value="12">12%</SelectItem>
                <SelectItem value="22">22%</SelectItem>
                <SelectItem value="24">24%</SelectItem>
                <SelectItem value="32">32%</SelectItem>
                <SelectItem value="35">35%</SelectItem>
                <SelectItem value="37">37%</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">State Tax Rate</Label>
            <div className="relative">
              <Input
                type="number"
                value={stateTaxRate}
                onChange={(e) => setStateTaxRate(Number(e.target.value))}
                min={0}
                max={15}
                step={0.5}
                className="h-9 pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                %
              </span>
            </div>
          </div>
        </div>

        {/* Tax Lots Table */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Positions with Losses</h4>
            <Button variant="ghost" size="sm" onClick={selectAll}>
              {selectedLots.size === taxLots.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          <div className="border rounded-lg divide-y max-h-64 overflow-auto">
            {taxLots.map(lot => (
              <div
                key={lot.id}
                className={cn(
                  "flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors cursor-pointer",
                  selectedLots.has(lot.id) && "bg-primary/5"
                )}
                onClick={() => toggleLot(lot.id)}
              >
                <Checkbox
                  checked={selectedLots.has(lot.id)}
                  onCheckedChange={() => toggleLot(lot.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{lot.ticker}</span>
                    <Badge variant={lot.isShortTerm ? "secondary" : "outline"} className="text-[10px]">
                      {lot.isShortTerm ? 'Short-term' : 'Long-term'}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {lot.shares.toFixed(2)} shares @ ${lot.purchasePrice.toFixed(2)} • Bought {formatDate(lot.purchaseDate)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono text-status-critical">
                    {formatCurrency(lot.unrealizedGainLoss)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    Current: ${lot.currentPrice.toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Wash Sale Warning */}
        {washSaleWarnings.size > 0 && (
          <div className="p-3 rounded-lg bg-status-warning/10 border border-status-warning/30 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-status-warning mt-0.5 shrink-0" />
            <div className="text-xs">
              <p className="font-medium text-status-warning">Wash Sale Warning</p>
              <p className="text-muted-foreground mt-0.5">
                {washSaleWarnings.size} selected position(s) are short-term. If you buy a substantially identical security
                within 30 days before or after the sale, the loss may be disallowed.
              </p>
            </div>
          </div>
        )}

        {/* Savings Summary */}
        {calculations.selectedCount > 0 && (
          <div className="p-4 rounded-lg bg-status-good/10 border border-status-good/30">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-5 w-5 text-status-good" />
              <span className="font-semibold">Estimated Tax Savings</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Total Losses</p>
                <p className="text-lg font-mono font-bold text-status-critical">
                  -{formatCurrency(calculations.totalLosses)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Short-Term Savings</p>
                <p className="text-lg font-mono font-bold text-status-good">
                  +{formatCurrency(calculations.shortTermSavings)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Long-Term Savings</p>
                <p className="text-lg font-mono font-bold text-status-good">
                  +{formatCurrency(calculations.longTermSavings)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Tax Savings</p>
                <p className="text-xl font-mono font-bold text-status-good">
                  +{formatCurrency(calculations.totalSavings)}
                </p>
              </div>
            </div>

            {calculations.excessLoss > 0 && (
              <div className="mt-3 pt-3 border-t border-status-good/30">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">{formatCurrency(calculations.excessLoss)}</span> exceeds the annual $3,000 limit
                  and will carry forward for <span className="font-medium">{calculations.yearsToFullyUtilize} years</span>.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {calculations.selectedCount > 0 && (
          <div className="flex gap-2">
            <Button className="flex-1" variant="outline">
              <TrendingDown size={14} className="mr-1.5" />
              Export Tax Report
            </Button>
            <Button className="flex-1">
              Review Replacement Securities
            </Button>
          </div>
        )}

        {/* Disclaimer */}
        <p className="text-[10px] text-muted-foreground text-center">
          This calculator provides estimates only. Tax situations vary.
          Consult a qualified tax professional before making tax-related investment decisions.
        </p>
      </CardContent>
    </Card>
  );
}
